import type { AdminLiveRoomGuestStatsResponse } from '@pulsify/shared/dto'
import * as m from '~/paraglide/messages'
import { METRIC_COLORS } from '~/shared/styles/chart-palette'
import { StackedBars, type StackedBarsSegment } from '~/shared/ui/stacked-bars/stacked-bars'
import { type GuestWeekKey, guestWeekBars, ratio } from './stats-derive'
import { formatAverage, formatCount, formatShare } from './stats-format'
import { MeterList, MeterRow } from './stats-tiles'

interface Props {
  stats: AdminLiveRoomGuestStatsResponse
  locale: string
}

function GuestWindows({ stats, locale }: Props) {
  const rows = [
    { key: 'last7d', label: m.statsWindow7d() },
    { key: 'last30d', label: m.statsWindow30d() },
    { key: 'total', label: m.statsRoomGuestsAllTime() },
  ] as const
  return (
    <table className="w-full text-sm tabular-nums">
      <thead>
        <tr className="text-muted-foreground text-xs">
          <th scope="col" className="pb-2 text-left font-normal">
            {m.statsRoomGuestsPeriod()}
          </th>
          <th scope="col" className="pb-2 text-right font-normal">
            {m.statsRoomGuests()}
          </th>
          <th scope="col" className="pb-2 text-right font-normal">
            {m.statsRoomGuestsWithAccount()}
          </th>
          <th scope="col" className="pb-2 text-right font-normal">
            {m.statsRoomGuestsRate()}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key} className="border-t">
            <th scope="row" className="py-2 text-left font-normal text-muted-foreground">
              {row.label}
            </th>
            <td className="py-2 text-right font-medium text-foreground">
              {formatCount(stats.guests[row.key], locale)}
            </td>
            <td className="py-2 text-right font-medium text-foreground">
              {formatCount(stats.converted[row.key], locale)}
            </td>
            <td className="py-2 text-right font-semibold font-serif text-foreground">
              {formatShare(stats.conversionRate[row.key], locale)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function GuestWeeks({ stats, locale }: Props) {
  const segments: StackedBarsSegment<GuestWeekKey>[] = [
    { key: 'converted', label: m.statsJoinersAccounts(), color: METRIC_COLORS.likes },
    {
      key: 'stillGuest',
      label: m.statsRoomGuestsStillGuest(),
      color: METRIC_COLORS.impressions,
    },
  ]
  return (
    <StackedBars
      series={guestWeekBars(stats.weekly)}
      segments={segments}
      bucket="week"
      title={m.statsRoomGuestsWeekly()}
      subtitle={m.statsRoomGuestsWeeklyExplain()}
      emptyText={m.statsRoomGuestsChartEmpty()}
      height={160}
      legend
      locale={locale}
    />
  )
}

function GuestEvents({ stats, locale }: Props) {
  return (
    <div className="space-y-3 border-t pt-4">
      <div className="space-y-1">
        <p className="font-medium text-foreground text-sm">{m.statsRoomGuestsTopEvents()}</p>
        <p className="text-muted-foreground text-xs">{m.statsRoomGuestsTopEventsHint()}</p>
      </div>
      {stats.topEvents.length === 0 ? (
        <p className="text-muted-foreground text-sm">{m.statsRoomGuestsNoEvents()}</p>
      ) : (
        <MeterList>
          {stats.topEvents.map((event) => {
            const share = ratio(event.converted, event.guestJoins)
            return (
              <MeterRow
                key={event.eventId}
                label={event.title}
                value={formatCount(event.guestJoins, locale)}
                share={share}
                shareText={formatShare(share, locale)}
                color={METRIC_COLORS.likes}
              />
            )
          })}
        </MeterList>
      )}
    </div>
  )
}

export function RoomGuestFigures({ stats, locale }: Props) {
  const { measured, averageHours, medianHours } = stats.timeToAccount
  return (
    <>
      <GuestWindows stats={stats} locale={locale} />
      {measured > 0 && (
        <p className="text-muted-foreground text-xs tabular-nums">
          {m.statsRoomGuestsTime({
            median: formatAverage(medianHours, locale),
            average: formatAverage(averageHours, locale),
            count: formatCount(measured, locale),
          })}
        </p>
      )}
      <GuestWeeks stats={stats} locale={locale} />
      <GuestEvents stats={stats} locale={locale} />
    </>
  )
}
